
interface CaseStudy {
  id: string;
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  results: Record<string, string>;
  testimonial: {
    quote: string;
    author: string;
    position: string;
  };
  image: string;
  isClientPhoto?: boolean;
}

interface CaseStudyCardProps {
  study: CaseStudy;
  reverse?: boolean;
}

export default function CaseStudyCard({ study, reverse = false }: CaseStudyCardProps) {
  const imageUrl = study.isClientPhoto 
    ? study.image 
    : `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28study.image%29%7D&width=600&height=400&seq=${study.id}&orientation=landscape`;

  return (
    <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center max-w-7xl mx-auto`}>
      {/* Image */}
      <div className="lg:w-1/2">
        <div className="relative">
          {study.isClientPhoto ? (
            <div className="w-full h-80 bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] rounded-lg shadow-lg flex items-center justify-center p-8">
              <img
                src={imageUrl}
                alt={`${study.testimonial.author} - ${study.industry} client`}
                className="w-48 h-48 rounded-full object-cover shadow-lg border-4 border-white"
              />
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={`${study.company} case study - ${study.industry} success story`}
              className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
            />
          )}
          <div className="absolute top-4 left-4 bg-[#2BB6A3] text-white px-3 py-1 rounded-full text-sm font-semibold">
            {study.industry}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="lg:w-1/2">
        <h3 className="text-3xl font-bold text-[#1B365D] mb-4">{study.company}</h3>
        
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-[#1B365D] mb-2">Challenge:</h4>
          <p className="text-gray-600">{study.challenge}</p>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-[#1B365D] mb-2">Solution:</h4>
          <p className="text-gray-600">{study.solution}</p>
        </div>

        <div className="mb-8">
          <h4 className="text-lg font-semibold text-[#1B365D] mb-4">Results:</h4>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(study.results).map(([key, value]) => (
              <div key={key} className="bg-[#2BB6A3]/10 rounded-lg p-4 text-center">
                <div className="font-bold text-[#2BB6A3] text-lg">{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <blockquote className="text-gray-700 italic mb-4">
            "{study.testimonial.quote}"
          </blockquote>
          <div className="flex items-center">
            <div>
              <div className="font-semibold text-[#1B365D]">{study.testimonial.author}</div>
              <div className="text-gray-600 text-sm">{study.testimonial.position}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
